/**
 * StudioGameCard — one card in the Studio "My games" grid. Unlike the gallery's
 * {@link GameCard} (a tile that links to the public `/g/:slug` play page), a
 * studio card is a WORKBENCH ROW — the sticker spec's `.gcard`: cover thumb
 * left, meta right (title, the visibility chip inline with build status), and
 * the publish action as a butter corner button pinned bottom-right.
 *
 * It links to the creator hub at `/studio/g/:id` (the Edit destination) and
 * surfaces work-surface signals — build status, an "Editor" badge for games you
 * don't own, and a "Finish setup" affordance for an unbuilt draft (which has no
 * hub to open yet), plus "Delete draft" when you own it, which is how a creator
 * at the draft cap makes room (the server allows nothing else to be deleted).
 *
 * The corner button is the COMPACT surface of the one publish machine
 * ({@link usePublishAction}) — same hook as the hub's PublishControl, never a
 * second `updateCreation` wiring. It stays deliberately terse: the full audit
 * story (failure detail, the explainer prose) belongs to the hub Overview,
 * which is one click away — the card IS a link to it.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isUnbuiltDraft, type Creation } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { slugGradient } from '../gallery/cover.js';
import { lastUpdatedLabel } from '../gallery/model.js';
import { isOwned } from './studioList.js';
import { publishState, PUBLISH_STATE_LABEL } from './publishState.js';
import { usePublishAction, type PublishActionKind } from './usePublishAction.js';
import { clearCreateWaiting, createWaitingBadge, isCreateWaiting } from './createWaiting.js';

/** Corner-button copy — the compact dialect of the hub's full sentences. */
const CORNER_LABEL: Record<PublishActionKind, string> = {
  publish: 'Publish',
  'publish-now': 'Publish now',
  'retry-publish': 'Retry',
  unpublish: 'Unpublish',
};

export function StudioGameCard({
  game,
  uid,
  onResume,
  onDeleted,
}: {
  game: Creation & { id: string };
  uid: string;
  /** Called when the creator clicks "Finish setup" on an unbuilt draft. */
  onResume: (id: string) => void;
  /** Called once the server has deleted this draft. */
  onDeleted: (id: string) => void;
}) {
  const navigate = useNavigate();
  const services = useServices();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const initial = game.title.charAt(0).toUpperCase();
  const editor = !isOwned(game, uid);
  const draft = isUnbuiltDraft(game);
  const updated = lastUpdatedLabel(game.updatedAt);
  // The PUBLISH chip is VISIBILITY, not build progress: an unbuilt wizard
  // resume (build-status draft, "Finish setup" below) is a different thing
  // from a visibility-'draft' private fork, and the chip never conflates them.
  const published = publishState(game.visibility);
  const publish = usePublishAction(game);
  const [waiting, setWaiting] = useState(() => {
    try { return isCreateWaiting(window.localStorage, uid, game.id); } catch { return false; }
  });
  const waitingBadge = createWaitingBadge(waiting, game);
  function openedGame() {
    if (!waiting || game.activeVersion === 0) return;
    try { clearCreateWaiting(window.localStorage, uid, game.id); } catch { /* Badge stays. */ }
    setWaiting(false);
  }

  async function deleteDraft() {
    setDeleting(true);
    setDeleteError('');
    try {
      await services.callable.deleteCreation({ creationId: game.id });
      onDeleted(game.id);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete draft.');
      setDeleting(false);
    }
  }

  const thumb = (
    <div
      className={`gcard__thumb${game.coverUrl ? ' gcard__thumb--cover' : ''}`}
      style={game.coverUrl ? undefined : { background: slugGradient(game.slug) }}
    >
      {game.coverUrl && (
        <img className="gcard__cover" src={game.coverUrl} alt="" loading="lazy" aria-hidden="true" />
      )}
      {editor && (
        <span className="gcard__editor-flag" title="You edit this game — you are not its owner">
          Editor
        </span>
      )}
      {!game.coverUrl && initial}
    </div>
  );

  const body = (
    <div className="gcard__meta">
      <div className="gcard__title">{game.title}</div>
      {waitingBadge && <span className="pill" data-testid="create-waiting-badge" data-state={game.activeVersion > 0 ? 'ready' : 'waiting'}>{waitingBadge}</span>}
      <div className="gcard__line">
        <span
          className="pill"
          data-testid="visibility-chip"
          data-state={published}
          title={
            published === 'published'
              ? 'Listed in the gallery'
              : published === 'private-fork'
                ? 'A fork stays private to its members until its first shipped change'
                : 'Not listed in the gallery — anyone with the link can play'
          }
        >
          {PUBLISH_STATE_LABEL[published]}
        </span>
        <span className={`status-dot status-dot--${game.status}`} /> {game.status}
        {game.latestVersion > 0 && <span>v{game.latestVersion}</span>}
        {updated && (
          <span data-testid="card-updated">
            <span aria-hidden="true">· </span>updated {updated}
          </span>
        )}
      </div>
      <div className="gcard__line gcard__line--stats">
        <span>{game.playsCount ?? 0} plays</span>
        <span>{game.viewsCount ?? 0} views</span>
        <span>{game.likesCount} ♥</span>
      </div>
      <div className="gcard__actions">
        {draft ? (
          <>
          <button
            className="btn btn--sm"
            data-testid="finish-setup"
            onClick={(e) => {
              e.preventDefault();
              onResume(game.id);
            }}
          >
            Finish setup
          </button>
          {!editor && !confirmDelete && (
            <button
              className="btn btn--sm btn--danger"
              data-testid="delete-draft"
              onClick={() => setConfirmDelete(true)}
            >
              Delete draft
            </button>
          )}
          {!editor && confirmDelete && (
            <div role="group" aria-label={`Confirm deleting ${game.title}`}>
              <p>Delete this draft? This can't be undone.</p>
              <button
                className="btn btn--sm btn--danger"
                data-testid="delete-draft-confirm"
                disabled={deleting}
                onClick={() => void deleteDraft()}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                className="btn btn--sm btn--ghost"
                data-testid="delete-draft-cancel"
                disabled={deleting}
                onClick={() => setConfirmDelete(false)}
              >
                Keep
              </button>
            </div>
          )}
          {deleteError && <div className="error-text" role="alert">{deleteError}</div>}
          </>
        ) : (
          <button
            className="btn btn--sm btn--primary"
            data-testid="studio-edit"
            onClick={(e) => {
              e.preventDefault();
              openedGame();
              navigate(`/studio/g/${game.id}`);
            }}
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );

  // The corner Publish button. NOTHING is offered for an unbuilt draft (there
  // is no built version to audit yet) or a pre-divergence fork (the hook
  // already declines to offer an action that could only fail). While the audit
  // runs the corner shows a compact "test-playing" pill: it resolves ITSELF —
  // the hook's stamp watch auto-publishes, and the visibility flip arrives on
  // the studio list's own creations subscription, which re-renders this card
  // into its "Unpublish" state. Ordered LAST in the DOM (it is positioned, not
  // flowed) so Edit stays the row's first button for the QA harnesses.
  const corner = draft ? null : publish.action ? (
    <button
      className={`pubbtn${publish.action === 'unpublish' ? ' pubbtn--unpublish' : ''}${
        publish.phase.kind === 'error' || publish.phase.kind === 'audit-failed'
          ? ' pubbtn--alert'
          : ''
      }`}
      data-testid="card-publish"
      data-action={publish.action}
      data-phase={publish.phase.kind}
      disabled={publish.busy}
      title={
        publish.action === 'unpublish'
          ? 'Remove it from the gallery — anyone with the link can still play'
          : publish.action === 'publish-now'
            ? 'The playtest finished — publish it'
            : publish.action === 'retry-publish'
              ? `The playtest audit failed${publish.phase.kind === 'audit-failed' && publish.phase.message ? ` (${publish.phase.message})` : ''} — open the game for the full report, or retry here`
              : publish.phase.kind === 'error'
                ? publish.phase.message
                : 'List it in the gallery'
      }
      onClick={(e) => {
        // The card is a <Link> to the hub — the corner acts, never navigates.
        e.preventDefault();
        e.stopPropagation();
        publish.run();
      }}
    >
      {publish.busy ? 'Working…' : CORNER_LABEL[publish.action]}
    </button>
  ) : publish.phase.kind === 'pending' ? (
    <span
      className="pubbtn pubbtn--pending"
      data-testid="card-publish-pending"
      title="Every game gets a quick automated playtest before it goes public. It publishes on its own when the playtest lands — open the game to watch it."
    >
      Test-playing…
    </span>
  ) : null;

  const card = (
    <div className="gcard" data-slug={game.slug}>
      {thumb}
      {body}
      {corner}
    </div>
  );

  // An unbuilt draft has no hub — the whole card is inert except "Finish setup".
  // A built game's card is a link to its hub (Edit is the explicit affordance).
  // The attribution modal is a SIBLING of the card, never a child: it is a
  // fixed overlay, and inside the <Link> its clicks would bubble into a
  // navigation.
  return (
    <>
      {draft ? (
        <div
          className="card-link card-link--static"
          data-testid="studio-row"
          data-status={game.status}
          data-slug={game.slug}
          data-version={game.latestVersion}
          data-active-version={game.activeVersion}
          data-build-state={game.buildState ?? 'idle'}
        >
          {card}
        </div>
      ) : (
        <Link
          to={`/studio/g/${game.id}`}
          onClick={openedGame}
          className="card-link"
          data-testid="studio-row"
          data-status={game.status}
          data-slug={game.slug}
          data-version={game.latestVersion}
          data-active-version={game.activeVersion}
          data-build-state={game.buildState ?? 'idle'}
        >
          {card}
        </Link>
      )}
      {publish.attributionModal}
    </>
  );
}
